import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva } from "class-variance-authority";

import { cn } from "@/lib/utils"

const buttonVariants = cva(
  "relative inline-flex items-center justify-center gap-2.5 whitespace-nowrap overflow-hidden rounded-full border text-sm font-medium leading-none tracking-[0.02em] transition-[transform,box-shadow,background-color,border-color,color,filter] duration-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/70 focus-visible:ring-offset-2 focus-visible:ring-offset-background active:translate-y-[1px] disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default:
          "glass-button border-primary/50 bg-[linear-gradient(180deg,hsl(var(--primary)/0.92),hsl(var(--primary)/0.74))] text-primary-foreground shadow-[0_14px_30px_-16px_hsl(var(--primary)/0.95),inset_0_1px_0_rgba(255,255,255,0.44),inset_0_-14px_24px_rgba(5,9,18,0.24)] hover:-translate-y-[1px] hover:brightness-105 hover:shadow-[0_18px_34px_-14px_hsl(var(--primary)/0.95),inset_0_1px_0_rgba(255,255,255,0.48),inset_0_-16px_26px_rgba(5,9,18,0.26)]",
        destructive:
          "glass-button border-destructive/42 bg-[linear-gradient(180deg,hsl(var(--destructive)/0.76),hsl(var(--destructive)/0.62))] text-destructive-foreground shadow-[0_12px_24px_-16px_hsl(var(--destructive)/0.58),inset_0_1px_0_rgba(255,255,255,0.24),inset_0_-10px_16px_rgba(28,8,8,0.2)] hover:-translate-y-[1px] hover:brightness-103",
        outline:
          "glass-button border-border/75 bg-[linear-gradient(180deg,hsl(var(--card)/0.84),hsl(var(--card)/0.62))] text-foreground shadow-[0_12px_24px_-18px_rgba(0,0,0,0.72),inset_0_1px_0_rgba(255,255,255,0.35)] hover:-translate-y-[1px] hover:border-primary/38 hover:bg-[linear-gradient(180deg,hsl(var(--card)/0.94),hsl(var(--card)/0.72))]",
        secondary:
          "glass-button border-border/65 bg-[linear-gradient(180deg,hsl(var(--secondary)/0.92),hsl(var(--secondary)/0.75))] text-secondary-foreground shadow-[inset_0_1px_0_rgba(255,255,255,0.28),0_10px_20px_-16px_rgba(0,0,0,0.65)] hover:-translate-y-[1px] hover:bg-[linear-gradient(180deg,hsl(var(--secondary)/1),hsl(var(--secondary)/0.83))]",
        ghost: "border-transparent bg-transparent text-muted-foreground shadow-none hover:border-border/60 hover:bg-accent/65 hover:text-foreground",
        link: "border-transparent bg-transparent px-0 text-primary shadow-none underline-offset-4 hover:underline",
      },
      size: {
        default: "h-10 px-[1.4rem] py-2",
        sm: "h-9 px-[1.15rem] text-xs",
        lg: "h-11 px-[2.1rem] text-sm",
        icon: "h-10 w-10 p-0",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

const Button = React.forwardRef(({ className, variant, size, asChild = false, ...props }, ref) => {
  const Comp = asChild ? Slot : "button"
  return (
    <Comp
      className={cn(buttonVariants({ variant, size, className }))}
      ref={ref}
      {...props} />
  );
})
Button.displayName = "Button"

export { Button, buttonVariants }
